import { Injectable } from '@angular/core';
import { TNil } from '@app/app.interface';
import { environment } from '@core/environments/environment';
import { EEnvironmentName } from '@core/environments/environment.defaults';
import { EPlatform, EPlatformFeature } from '@enums/campaign.enum';
import { TSocialContent } from '@interfaces/campaign-analytics-api.interface';
import { IChannel } from '@pages/creators/creators.interface';
import { camelCase } from 'lodash';

@Injectable({
  providedIn: 'root',
})
export class SocialUtilitiesService {
  public static readonly platformColorMap = {
    [EPlatform.twitch]: 'var(--shikenso-social-twitch)',
    [EPlatform.instagram]: 'var(--shikenso-social-instagram)',
    [EPlatform.youtube]: 'var(--shikenso-social-youtube)',
    [EPlatform.tiktok]: 'var(--shikenso-social-tiktok)',
    [EPlatform.facebook]: 'var(--shikenso-social-facebook)',
    [EPlatform.x]: 'var(--shikenso-social-x)',
  } as const;

  public static generateSocialUrl(platform: EPlatform | EPlatformFeature, id: string): string {
    if (!platform || !id) return '';

    const platformFormatted = camelCase(platform) as EPlatform | EPlatformFeature;

    switch (platformFormatted) {
      case EPlatform.twitch:
      case EPlatformFeature.twitchStream:
        return `https://www.twitch.tv/${id}`;
      case EPlatform.instagram:
      case EPlatformFeature.instagramPost:
      case EPlatformFeature.instagramReel:
      case EPlatformFeature.instagramStory:
        return `https://www.instagram.com/${id}`;
      case EPlatform.youtube:
      case EPlatformFeature.youtubeStream:
      case EPlatformFeature.youtubeVod:
      case EPlatformFeature.youtubeShort:
        return `https://www.youtube.com/@${id}`;
      case EPlatform.facebook:
      case EPlatformFeature.facebookPost:
      case EPlatformFeature.facebookVideo:
        return `https://www.facebook.com/${id}`;
      case EPlatform.tiktok:
      case EPlatformFeature.tiktokPost:
        return `https://www.tiktok.com/@${id}`;
      case EPlatform.x:
      case EPlatformFeature.xPost:
        return `https://x.com/${id}`;
      default:
        return '';
    }
  }

  public static isSocialLive(socialContent: TSocialContent | TNil): boolean {
    if (!socialContent) {
      return false;
    }

    return !!(socialContent && 'end' in socialContent && socialContent.end === null);
  }

  public static getPlatform(platformFeature: EPlatform | EPlatformFeature): EPlatform {
    const platformFormatted = camelCase(platformFeature) as EPlatform | EPlatformFeature;

    switch (platformFormatted) {
      case EPlatformFeature.twitchStream:
      case EPlatform.twitch:
        return EPlatform.twitch;
      case EPlatformFeature.youtubeStream:
      case EPlatform.youtube:
        return EPlatform.youtube;
      case EPlatformFeature.youtubeVod:
        return EPlatform.youtube;
      case EPlatformFeature.youtubeShort:
        return EPlatform.youtube;
      case EPlatformFeature.instagramPost:
      case EPlatform.instagram:
        return EPlatform.instagram;
      case EPlatformFeature.instagramReel:
        return EPlatform.instagram;
      case EPlatformFeature.instagramStory:
        return EPlatform.instagram;
      case EPlatformFeature.tiktokPost:
      case EPlatform.tiktok:
        return EPlatform.tiktok;
      case EPlatformFeature.facebookPost:
      case EPlatform.facebook:
        return EPlatform.facebook;
      case EPlatformFeature.facebookVideo:
        return EPlatform.facebook;
      case EPlatformFeature.xPost:
      case EPlatform.x:
        return EPlatform.x;
    }
  }

  public static isPlatformCompatible(
    platformOrFeature: EPlatform | EPlatformFeature,
    creatorPlatforms?: EPlatform[] | Set<EPlatform>,
    creatorChannels?: IChannel[],
  ): boolean {
    if (creatorPlatforms) {
      if (Array.isArray(creatorPlatforms)) {
        return creatorPlatforms.includes(this.getPlatform(platformOrFeature));
      }

      return creatorPlatforms.has(this.getPlatform(platformOrFeature));
    }

    if (creatorChannels) {
      return creatorChannels.some(
        (channel) => this.getPlatform(channel.platform as EPlatform) === this.getPlatform(platformOrFeature),
      );
    }

    return false;
  }

  public static arePlatformsEqual(
    platform1: EPlatform | EPlatformFeature,
    platform2: EPlatform | EPlatformFeature,
  ): boolean {
    return this.getPlatform(platform1) === this.getPlatform(platform2);
  }

  public static isNonStreamPlatformFeature(platformFeature: EPlatformFeature): boolean {
    const feature = camelCase(platformFeature) as EPlatformFeature;

    return feature !== EPlatformFeature.twitchStream && feature !== EPlatformFeature.youtubeStream;
  }

  public static getInstagramAuthUrl(): string | null {
    const { environmentName } = environment;
    let kundenName = '';

    if (environmentName === EEnvironmentName.production) {
      kundenName = 'campaigns_prod';
    } else if (environmentName === EEnvironmentName.test) {
      kundenName = 'campaigns_test';
    }

    if (!kundenName) {
      return null;
    }

    return `https://auth.shikenso.com/?kunde=${kundenName}&platform=facebook`;
  }

  public static getInstagramAuthGuideUrl(): string | null {
    return 'https://spm.shikenso.com/files/shikenso_meta_authorisation-guide.pdf';
  }
}
